import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { Subject, Subscription, of } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  timeout,
  finalize,
  switchMap,
} from "rxjs/operators";

import { PermissionsService } from "src/app/core/services/permissions.service";
import { DealsService } from "src/app/pages/deals/services/deals.service";
import { EntityService } from "src/app/pages/entity/services/entity.service";
import { PropertiesService } from "src/app/pages/properties/services/properties.services";
import { ProposalsService } from "src/app/pages/proposals/services/proposals.service";
import { SalesProcessingService } from "src/app/pages/sales-processing/services/sales-processing.service";
import { environment } from "src/environments/environment";
import Swal from "sweetalert2";

@Component({
  selector: "app-sale-form",
  templateUrl: "./sale-form.component.html",
  styleUrls: ["./sale-form.component.scss"],
})
export class SaleFormComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
  // ── Dumb-mode inputs ──────────────────────────────────────────────────
  @Input() formGroup!: FormGroup;
  @Input() typeRent: any;
  @Input() isRentMode = false;
  @Input() property: any;
  @Input() paymentsConditions: any[] = [];
  @Input() pathProfileImage = "";
  @Input() openNewAgent: { listing: boolean; selling: boolean } = {
    listing: false,
    selling: false,
  };
  @Input() selected: { listing: any; selling: any } = {
    listing: null,
    selling: null,
  };
  @Input() agentsExceptListing: any[] = [];
  @Input() agentsExceptSelling: any[] = [];
  @Input() showAgentSections = true;
  @Input() isExternalProperty = false;
  @Input() showSaleWarning = false;
  @Input() disableBySaleInProgress = false;
  @Input() allowSharedSellerHide = true;
  @Input() lockDealSelection = false;
  @Input() showReferralCommissionSection = false;
  @Input() referralCommissionEditable = false;
  @Input() sellerValidationSubmitted = false;
  @Input() referralCommissionNoticeKey: string | null = null;
  @Input() referralCommissionInitial: {
    source?: "AGENCY_COMMISSION" | "AGENT_COMMISSION" | null;
    valueType?: "PERCENTAGE" | "FIXED" | null;
    value?: any;
  } | null = null;
  @Input() isProposalCheck = false;
  @Input() proposal: any;
  @Input() getAgentsFn: (type: "listing" | "selling") => any[] = () => [];
  @Input() getStoreByIdFn: (id: any) => string = () => "";
  @Input() getSuffixFn: () => string = () => "€";
  /**
   * Realça comissões de agente por preencher (vazias ou 0) e agentes em falta.
   * Só visual — quem bloqueia o submit é o ecrã que embebe o formulário.
   * Ligado por omissão (o realce inline evita o erro só ao gravar); um host
   * pode desligar passando false.
   */
  @Input() highlightMissingCommissions = true;
  /**
   * Negócio já escolhido pelo ecrã que embebe o formulário (ex.: o próprio
   * negócio no modal "Ganho"). Sem isto o cartão "Negócio associado" só tinha
   * dados depois de se abrir o seletor, porque a lista é carregada aí.
   */
  @Input() preselectedDeal: any = null;

  // ── Dumb-mode outputs ─────────────────────────────────────────────────
  @Output() addAgent = new EventEmitter<"listing" | "selling">();
  @Output() removeAgent = new EventEmitter<{ type: "listing" | "selling"; id: any }>();
  @Output() changeAgent = new EventEmitter<{
    type: "listing" | "selling";
    id: any;
    agent?: any;
  }>();
  @Output() addPaymentCondition = new EventEmitter<void>();
  @Output() removePaymentCondition = new EventEmitter<number>();
  @Output() paymentChange = new EventEmitter<any>();
  @Output() paymentDropped = new EventEmitter<any>();

  // ── Smart-modal output ────────────────────────────────────────────────
  @Output() proposalChecked = new EventEmitter<{
    proposalId: any;
    proposal: any;
    saleId: any;
    formValues: any;
    listingAgents: any[];
    sellingAgents: any[];
    propertyData: any;
  }>();

  // ── Smart-modal internal refs & state ────────────────────────────────
  @ViewChild("checkProposalModal") checkProposalModalRef: any;
  @ViewChild("agentsModalInternal") agentsModalInternalRef: any;
  @ViewChild("dealPickerModal") dealPickerModalRef: any;

  internalForm!: FormGroup;
  proposalData: any;
  propertyDataModal: any;
  internalAllAgents: any[] = [];
  internalListingSelected: any[] = [];
  internalSellingSelected: any[] = [];
  selectedListingAgents: any[] = [];
  selectedSellingAgents: any[] = [];
  agentsExceptListingFiltered: any[] = [];
  private selectedListingAgentsSignature = "";
  private selectedSellingAgentsSignature = "";
  submitedCheck = false;
  requiredDealForSale = false;
  requiredSellerForSale = false;
  dealsList: any[] = [];
  selectedDealDetails: any | null = null;
  dealsTotal = 0;
  dealsPage = 1;
  readonly dealsPageSize = 25;
  hasMoreDeals = false;
  loadingDeals = false;
  selectedDealStatusId: number | null = null;
  dealStatusOptions: { id: number; name: string }[] = [];
  dealAssociationFilter: "related" | "unrelated" = "related";
  agentsSelectedTypeInternal: "listing" | "selling" = "listing";
  pathProfileImageInternal = `${environment.apiUrl}/${environment.dirProfileImage}/`;
  dealSearch = "";
  private readonly dealSearchSubject = new Subject<string>();
  private readonly commissionTargetPreviewSubject = new Subject<void>();
  private dealSearchSubscription?: Subscription;
  private commissionTargetPreviewSubscription?: Subscription;
  private formGroupValueSubscription?: Subscription;
  private internalFormValueSubscription?: Subscription;
  private pendingDealsReload = false;
  private hasLoadedEntitySettings = false;
  private readonly masterMinDate = new Date(1900, 0, 1);
  private readonly todayDate = this.getDateStart(new Date());
  private readonly configuredSaleDateControls = new WeakSet<AbstractControl>();
  private readonly configuredShareModeForms = new WeakSet<AbstractControl>();
  internalShareStores: any[] = [];
  loadingInternalShareTargets = false;
  private allInternalShareStores: any[] = [];
  private internalShareConsultantsByStore = new Map<number, any[]>();
  private internalShareTargetsLoaded = false;
  private isMasterUser = false;
  commissionTargetPreviewLoading = false;
  /** Assinatura do último payload de preview APLICADO — evita repetir o pedido. */
  private lastCommissionTargetPreviewSignature: string | null = null;
  private readonly saleDateRoleValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    if (this.canSelectPastSaleDate()) {
      return null;
    }

    // Allow editing other fields in existing sales with past date.
    if (control?.pristine) {
      return null;
    }

    const selectedDate = this.parseDateValue(control?.value);
    if (!selectedDate) {
      return null;
    }

    return selectedDate < this.todayDate
      ? { saleDatePastNotAllowed: true }
      : null;
  };

  constructor(
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private cookieService: CookieService,
    private permissionsService: PermissionsService,
    private entityService: EntityService,
    private dealsService: DealsService,
    private propertiesService: PropertiesService,
    private proposalsService: ProposalsService,
    private salesProcessingService: SalesProcessingService,
  ) {}

  ngOnInit(): void {
    this.isMasterUser = this.resolveIsMasterUser();
    this.dealSearchSubscription = this.dealSearchSubject
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe(() => {
        this.reloadDealsForSale();
      });

    this.commissionTargetPreviewSubscription =
      this.commissionTargetPreviewSubject
        .pipe(
          debounceTime(150),
          switchMap(() => {
            const payload = this.buildCommissionTargetPreviewPayload();

            if (!payload) {
              this.lastCommissionTargetPreviewSignature = null;
              this.applyCommissionTargetPreview([]);
              return of(null);
            }

            // O valueChanges do form agenda para QUALQUER campo, incluindo os
            // que não entram no cálculo — payload igual ao último aplicado
            // não repete o pedido (era isto que "travava" edições rápidas).
            const signature = JSON.stringify(payload);
            if (signature === this.lastCommissionTargetPreviewSignature) {
              return of(undefined);
            }

            this.commissionTargetPreviewLoading = true;

            return this.salesProcessingService
              .previewCommissionTarget(payload)
              .pipe(
                map((preview: any) => ({ preview, signature })),
                catchError(() => of(null)),
                finalize(() => {
                  this.commissionTargetPreviewLoading = false;
                }),
              );
          }),
        )
        .subscribe((result: any) => {
          if (result === undefined) {
            return;
          }
          if (result === null) {
            this.applyCommissionTargetPreview([]);
            return;
          }
          this.lastCommissionTargetPreviewSignature = result.signature;
          this.applyCommissionTargetPreview(result.preview?.agents ?? []);
        });

    this.loadEntitySettings();
    this.refreshAgentsExceptListingFiltered();
    this.refreshSelectedAgentsForTemplate();
    this.ensureShareModeControls();
    this.bindPreviewToExternalForm();
    this.syncSaleDateValidation();
    this.syncReferralCommissionControls();
    this.scheduleCommissionTargetPreview();
  }

  ngOnDestroy(): void {
    this.dealSearchSubscription?.unsubscribe();
    this.commissionTargetPreviewSubscription?.unsubscribe();
    this.formGroupValueSubscription?.unsubscribe();
    this.internalFormValueSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const changedKeys = Object.keys(changes);
    const callbackInputs = ["getAgentsFn", "getStoreByIdFn", "getSuffixFn"];
    const onlyCallbackInputsChanged =
      changedKeys.length > 0 &&
      changedKeys.every((key) => callbackInputs.includes(key));

    if (onlyCallbackInputsChanged) {
      return;
    }

    if (!this.lockDealSelection && !this.hasLoadedEntitySettings) {
      this.loadEntitySettings();
    }

    this.refreshAgentsExceptListingFiltered();
    this.refreshSelectedAgentsForTemplate();
    this.syncDealFieldValidation();
    this.ensureShareModeControls();
    this.syncSaleDateValidation();

    if (changes["formGroup"]) {
      this.bindPreviewToExternalForm();
    }

    this.syncReferralCommissionControls();
    this.scheduleCommissionTargetPreview();
  }

  // ─── Dumb-mode helpers ───────────────────────────────────────────────
  ngDoCheck(): void {
    this.refreshSelectedAgentsForTemplate();
  }

  get activeFormForTemplate(): FormGroup | null {
    return this.getActiveForm();
  }

  get form() {
    return this.activeFormForTemplate?.controls ?? {};
  }

  get isLocked(): boolean {
    return (
      this.disableBySaleInProgress === true && this.property?.saleInProgress
    );
  }

  get isSaleDateLocked(): boolean {
    if (this.isLocked) {
      return true;
    }

    // In edit mode keep original date visible but immutable for non-master users.
    return (
      !this.canSelectPastSaleDate() &&
      !this.internalForm &&
      !!this.property?.saleInProgress
    );
  }

  get saleDateMinDate(): Date {
    if (this.isSaleDateLocked) {
      return this.masterMinDate;
    }

    return this.canSelectPastSaleDate() ? this.masterMinDate : this.todayDate;
  }

  get internalFormControls() {
    return this.internalForm?.controls ?? {};
  }

  get canEditReferralCommission(): boolean {
    return this.referralCommissionEditable === true && !this.isLocked;
  }

  getAgents(type: "listing" | "selling"): any[] {
    const data = this.getAgentsFn(type);
    return Array.isArray(data) ? data : [];
  }

  /** Avatar do agente com fallback (agentFile vazio/nulo dava um src partido). */
  agentAvatar(agent: any): string {
    const file = agent?.agentFile ?? agent?.image ?? null;
    const base = this.pathProfileImage || this.pathProfileImageInternal;
    return file ? `${base}${file}` : "assets/images/users/user-dummy-img.jpg";
  }

  onAvatarError(event: Event): void {
    const img = event?.target as HTMLImageElement | null;
    if (img && !img.src.endsWith("user-dummy-img.jpg")) {
      img.src = "assets/images/users/user-dummy-img.jpg";
    }
  }

  trackByAgentId(index: number, agent: any): any {
    return agent?.id ?? agent?.userId ?? agent?.agentId ?? index;
  }

  getStoreById(id: any): string {
    return this.getStoreByIdFn(id);
  }

  getSuffix(): string {
    return this.getSuffixFn();
  }

  showSellerSection(): boolean {
    if (!this.allowSharedSellerHide) return true;
    if (this.form["sharedSale"]?.value === true) return false;
    return true;
  }

  requiresSellerForSale(): boolean {
    const activeForm = this.getActiveForm();
    return (
      this.requiredSellerForSale === true &&
      this.showAgentSections === true &&
      this.showSellerSection() &&
      activeForm?.get("sharedSale")?.value !== true
    );
  }

  isSellerRequiredInvalid(): boolean {
    const sellingAgentsLength = this.internalForm
      ? this.internalSellingSelected.length
      : this.selectedSellingAgents.length;

    return (
      this.requiresSellerForSale() &&
      (this.submitedCheck || this.sellerValidationSubmitted) &&
      sellingAgentsLength === 0
    );
  }

  onToggleSharedSale(): void {
    this.ensureShareModeControls();
    this.syncLegacyAgencyProposalFlag();
  }

  formatCommissionTargetPercent(value: any): string {
    const percent = Number(value ?? 0);

    if (!Number.isFinite(percent)) {
      return "0";
    }

    const locale =
      `${this.translateService.currentLang || this.translateService.defaultLang}`.trim() ||
      "pt-PT";

    return percent.toLocaleString(locale, {
      minimumFractionDigits: percent % 1 === 0 ? 0 : 1,
      maximumFractionDigits: 2,
    });
  }

  getTranslatedCommissionTargetChangedMessage(agent: any): string {
    return this.translateService.instant("t-commission-target-updated", {
      percent: this.formatCommissionTargetPercent(
        agent?.commissionTargetCurrentPercent,
      ),
    });
  }

  getTranslatedCommissionTargetNextSaleMessage(agent: any): string {
    return this.translateService.instant("t-commission-target-next-sale", {
      percent: this.formatCommissionTargetPercent(
        agent?.commissionTargetNextPercent,
      ),
    });
  }

  onAddAgent(type: "listing" | "selling") {
    this.addAgent.emit(type);
  }

  onRemoveAgent(type: "listing" | "selling", id: any) {
    this.removeAgent.emit({ type, id });
    this.scheduleCommissionTargetPreviewAsync();
  }

  onChangeAgent(id: any, type: "listing" | "selling", agent?: any) {
    const selectedAgent =
      agent ??
      (type === "listing" ? this.agentsExceptListingFiltered : this.agentsExceptSelling)
        ?.find((candidate: any) => `${candidate?.id ?? ""}` === `${id ?? ""}`);
    if (type === "listing" && this.isStoreOperatorAgent(selectedAgent)) {
      return;
    }

    this.changeAgent.emit({ type, id, agent });
    this.scheduleCommissionTargetPreviewAsync();
  }

  private refreshAgentsExceptListingFiltered(): void {
    this.agentsExceptListingFiltered =
      this.agentsExceptListing?.filter(
        (agent: any) => !this.isStoreOperatorAgent(agent),
      ) ?? [];
  }

  private refreshSelectedAgentsForTemplate(): void {
    const listingAgents = this.getAgents("listing");
    const sellingAgents = this.getAgents("selling");
    const listingSignature = this.getAgentsSignature(listingAgents);
    const sellingSignature = this.getAgentsSignature(sellingAgents);

    if (listingSignature !== this.selectedListingAgentsSignature) {
      this.selectedListingAgents = listingAgents;
      this.selectedListingAgentsSignature = listingSignature;
    }

    if (sellingSignature !== this.selectedSellingAgentsSignature) {
      this.selectedSellingAgents = sellingAgents;
      this.selectedSellingAgentsSignature = sellingSignature;
    }
  }

  private getAgentsSignature(agents: any[]): string {
    return (agents ?? [])
      .map(
        (agent: any) =>
          `${agent?.id ?? agent?.userId ?? agent?.agentId ?? ""}:${
            agent?.isListingAgent === true ? 1 : 0
          }:${agent?.isSellingAgent === true ? 1 : 0}`,
      )
      .join("|");
  }

  // A tabela de pagamentos NÃO agenda o preview da meta de comissão: o
  // cálculo do servidor não lê paymentConditions, e agendar a cada tecla
  // bloqueava a edição rápida (o botão Guardar fica desativado em loading).
  onAddPaymentCondition() {
    this.addPaymentCondition.emit();
  }

  onRemovePaymentCondition(index: number) {
    this.removePaymentCondition.emit(index);
  }

  onPaymentValueChange(payment: any) {
    this.paymentChange.emit(payment);
  }

  onPaymentsDrop(event: any) {
    this.paymentDropped.emit(event);
  }

  onAgentCommissionInputChanged() {
    this.scheduleCommissionTargetPreview();
  }

  // ─── Tabela de pagamentos: pré-visualização e comissão em valor fixo ─────
  //
  // Estado de UI por linha (modo % / € da comissão da agência) guardado à
  // parte do objeto `payment`: os ecrãs que embebem este formulário enviam o
  // objeto tal como está para DTOs com whitelist, e uma propriedade nova
  // faria o pedido falhar. O que se grava continua a ser `agencyValue` em %.
  private agencyModeByPayment = new WeakMap<object, "PERCENTAGE" | "FIXED">();
  private agencyFixedDraft = new WeakMap<object, string>();

  /** Número a partir do que a máscara deixa no modelo ("1 250,5" → 1250.5). */
  parseAmount(raw: any): number | null {
    if (raw === null || raw === undefined || raw === "") return null;
    if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
    const cleaned = `${raw}`.replace(/\s/g, "").replace(",", ".");
    const value = Number(cleaned);
    return Number.isFinite(value) ? value : null;
  }

  /** Valor de venda do formulário ativo (base das percentagens do comprador). */
  get saleBaseAmount(): number | null {
    return this.parseAmount(this.form["saleValue"]?.value);
  }

  /** Comissão total da agência (€) — mesma conta do servidor. */
  get storeCommissionAmount(): number | null {
    const base = this.saleBaseAmount;
    const commission = this.parseAmount(this.form["saleCommission"]?.value);
    if (commission === null) return null;
    const isPercentage = this.form["isPercentageCommission"]?.value !== false;
    if (!isPercentage) return commission;
    return base === null ? null : (base * commission) / 100;
  }

  /** € que o comprador paga nesta fase. */
  buyerAmount(payment: any): number | null {
    const value = this.parseAmount(payment?.value);
    if (value === null) return null;
    if (payment?.valueType === "PERCENTAGE") {
      const base = this.saleBaseAmount;
      return base === null ? null : (base * value) / 100;
    }
    return value;
  }

  /** % do valor de venda que o comprador paga nesta fase. */
  buyerPercent(payment: any): number | null {
    const value = this.parseAmount(payment?.value);
    if (value === null) return null;
    if (payment?.valueType === "PERCENTAGE") return value;
    const base = this.saleBaseAmount;
    return base ? (value / base) * 100 : null;
  }

  /** € de comissão que a agência recebe nesta fase (agencyValue é % da comissão). */
  agencyAmount(payment: any): number | null {
    const percent = this.parseAmount(payment?.agencyValue);
    const total = this.storeCommissionAmount;
    if (percent === null || total === null) return null;
    return (total * percent) / 100;
  }

  agencyMode(payment: any): "PERCENTAGE" | "FIXED" {
    return (payment && this.agencyModeByPayment.get(payment)) ?? "PERCENTAGE";
  }

  setAgencyMode(payment: any, mode: "PERCENTAGE" | "FIXED"): void {
    if (!payment) return;
    this.agencyModeByPayment.set(payment, mode);
    if (mode === "FIXED") {
      const amount = this.agencyAmount(payment);
      this.agencyFixedDraft.set(
        payment,
        amount === null ? "" : `${Math.round(amount * 100) / 100}`,
      );
    }
  }

  /** Valor fixo (€) que o utilizador está a escrever para a agência nesta fase. */
  agencyFixedValue(payment: any): string {
    if (!payment) return "";
    if (this.agencyFixedDraft.has(payment)) {
      return this.agencyFixedDraft.get(payment) ?? "";
    }
    const amount = this.agencyAmount(payment);
    return amount === null ? "" : `${Math.round(amount * 100) / 100}`;
  }

  /**
   * € fixo → % da comissão total, que é o que se grava (o servidor e a
   * faturação só conhecem `agencyValue` em %). Sem comissão total definida
   * não há como converter — o campo fica à espera.
   */
  onAgencyFixedInput(payment: any, raw: any): void {
    if (!payment) return;
    this.agencyFixedDraft.set(payment, `${raw ?? ""}`);
    const fixed = this.parseAmount(raw);
    const total = this.storeCommissionAmount;
    if (fixed === null || !total) return;
    payment.agencyValue = `${Math.round((fixed / total) * 10000) / 100}`;
    this.onPaymentValueChange(payment);
  }

  get buyerTotalPercent(): number | null {
    let sum = 0;
    let any = false;
    for (const payment of this.paymentsConditions ?? []) {
      const percent = this.buyerPercent(payment);
      if (percent === null) continue;
      any = true;
      sum += percent;
    }
    return any ? Math.round(sum * 100) / 100 : null;
  }

  get buyerTotalAmount(): number | null {
    let sum = 0;
    let any = false;
    for (const payment of this.paymentsConditions ?? []) {
      const amount = this.buyerAmount(payment);
      if (amount === null) continue;
      any = true;
      sum += amount;
    }
    return any ? sum : null;
  }

  /** Valor de venda − total do comprador (para o tile "diferença"). */
  get buyerTotalDifference(): number | null {
    const base = this.saleBaseAmount;
    const total = this.buyerTotalAmount;
    if (base === null || total === null) return null;
    return base - total;
  }

  get agencyTotalPercent(): number | null {
    let sum = 0;
    let any = false;
    for (const payment of this.paymentsConditions ?? []) {
      const percent = this.parseAmount(payment?.agencyValue);
      if (percent === null) continue;
      any = true;
      sum += percent;
    }
    return any ? Math.round(sum * 100) / 100 : null;
  }

  get agencyTotalAmount(): number | null {
    const total = this.storeCommissionAmount;
    const percent = this.agencyTotalPercent;
    return total === null || percent === null ? null : (total * percent) / 100;
  }

  /** Fora de ±0,5% do esperado — mostra-se aviso, não se bloqueia. */
  get buyerTotalOff(): boolean {
    const percent = this.buyerTotalPercent;
    return percent !== null && Math.abs(percent - 100) > 0.5;
  }

  get agencyTotalOff(): boolean {
    const percent = this.agencyTotalPercent;
    return percent !== null && Math.abs(percent - 100) > 0.5;
  }

  /** Comissão do agente por preencher (só realce; sem meta automática). */
  isAgentCommissionMissing(agent: any, type: "listing" | "selling"): boolean {
    if (!this.highlightMissingCommissions || agent?.commissionTargetEnabled) {
      return false;
    }
    const raw = type === "listing" ? agent?.listingCommission : agent?.saleCommission;
    const value = this.parseAmount(raw);
    return value === null || value <= 0;
  }

  openDealPicker(): void {
    this.dealSearch = "";
    this.selectedDealStatusId = null;
    this.dealAssociationFilter = "related";
    this.reloadDealsForSale();
    if (!this.dealPickerModalRef) {
      return;
    }
    this.modalService.open(this.dealPickerModalRef, {
      size: "lg",
      centered: true,
      backdropClass: "z-modal",
      windowClass: "z-modal",
      scrollable: true,
    });
  }

  onDealSearchChange(value: string): void {
    this.dealSearch = value ?? "";
    this.dealSearchSubject.next(this.dealSearch);
  }

  setDealStatusFilter(statusId: number | null): void {
    this.selectedDealStatusId = statusId;
    this.reloadDealsForSale();
  }

  setDealAssociationFilter(value: "related" | "unrelated"): void {
    this.dealAssociationFilter = value;
    this.reloadDealsForSale();
  }

  onDealsScroll(event: Event): void {
    const target = event?.target as HTMLElement | null;
    if (!target || this.loadingDeals || !this.hasMoreDeals) {
      return;
    }

    const threshold = 120;
    const isNearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;

    if (isNearBottom) {
      this.loadDealsForSale();
    }
  }

  selectDealForSale(deal: any, modal?: any): void {
    const form = this.getActiveForm();
    const control = this.ensureDealControl(form);
    if (!control) {
      return;
    }

    control.setValue(deal?.id ?? null);
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();
    this.selectedDealDetails = deal ?? null;
    modal?.close();
  }

  clearSelectedDeal(): void {
    const form = this.getActiveForm();
    const control = this.ensureDealControl(form);
    if (!control) {
      return;
    }

    control.setValue(null);
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();
    this.selectedDealDetails = null;
  }

  getSelectedDeal(): any | null {
    const selectedId = this.getActiveForm()?.get("dealId")?.value;
    if (!selectedId) {
      return null;
    }

    const selected = this.dealsList.find(
      (deal) => Number(deal?.id) === Number(selectedId),
    );
    if (selected) {
      return selected;
    }

    if (
      this.selectedDealDetails &&
      Number(this.selectedDealDetails?.id) === Number(selectedId)
    ) {
      return this.selectedDealDetails;
    }

    if (
      this.preselectedDeal &&
      Number(this.preselectedDeal?.id) === Number(selectedId)
    ) {
      return this.preselectedDeal;
    }

    return {
      id: selectedId,
      name: `${this.translateService.instant("t-deal")} #${selectedId}`,
      statusName: this.translateService.instant("t-selected"),
      property: { reference: "-", title: "-" },
      __isFallback: true,
    };
  }

  getFilteredDeals(): any[] {
    return this.dealsList;
  }

  getRelatedDeals(): any[] {
    return this.dealsList.filter((deal) =>
      this.isDealRelatedToCurrentProperty(deal),
    );
  }

  getUnrelatedDeals(): any[] {
    return this.dealsList.filter(
      (deal) => !this.isDealRelatedToCurrentProperty(deal),
    );
  }

  getVisibleDeals(): any[] {
    if (this.dealAssociationFilter === "unrelated") {
      return this.getUnrelatedDeals();
    }
    return this.getRelatedDeals();
  }

  getDealAgentName(deal: any): string {
    return deal?.agent?.name || "-";
  }

  getDealAgentImage(deal: any): string {
    return deal?.agent?.image
      ? this.pathProfileImageInternal + deal.agent.image
      : "assets/images/users/user-dummy-img.jpg";
  }

  getDealContactEmail(deal: any): string {
    return deal?.contact?.email || deal?.email || "-";
  }

  getDealContactPhone(deal: any): string {
    return deal?.contact?.phone || deal?.phone || "-";
  }

  getDealReceivedDate(deal: any): any {
    return deal?.createdAt || deal?.dateHour || null;
  }

  isDealRelatedToCurrentProperty(deal: any): boolean {
    if (!deal) {
      return false;
    }

    const currentPropertyId = this.getCurrentPropertyId();
    if (!currentPropertyId) {
      return true;
    }

    if (typeof deal?.isRelatedToProperty === "boolean") {
      return deal.isRelatedToProperty;
    }

    const dealPropertyId = deal?.property?.id ?? deal?.propertyId ?? null;
    if (!dealPropertyId) {
      return false;
    }

    return `${dealPropertyId}` === `${currentPropertyId}`;
  }

  isSelectedDealRelatedToCurrentProperty(): boolean {
    const selected = this.getSelectedDeal();
    if (!selected || selected?.__isFallback) {
      return true;
    }
    return this.isDealRelatedToCurrentProperty(selected);
  }

  trackByDealId(_: number, deal: any): any {
    return deal?.id;
  }

  getDealContactName(deal: any): string {
    return (
      deal?.contact?.fullName ||
      deal?.contact?.name ||
      `${deal?.contact?.firstName ?? ""} ${deal?.contact?.lastName ?? ""}`.trim() ||
      deal?.name ||
      "-"
    );
  }

  getDealPropertyRef(deal: any): string {
    return (
      deal?.externalProperty?.reference ||
      deal?.externalProperty?.ref ||
      deal?.property?.reference ||
      deal?.property?.ref ||
      "-"
    );
  }

  getDealPropertyTitle(deal: any): string {
    return (
      deal?.externalProperty?.title ||
      deal?.externalProperty?.address ||
      deal?.property?.title ||
      deal?.property?.address ||
      "-"
    );
  }

  getDealStatusName(deal: any): string {
    return `${deal?.statusName || "-"}`.toUpperCase();
  }

  isDealRequiredInvalid(): boolean {
    const control = this.ensureDealControl(this.getActiveForm());
    return !!(
      control &&
      control.errors?.["invalidDeal"] &&
      (control.touched || this.submitedCheck)
    );
  }

  // ─── Smart-modal public API ──────────────────────────────────────────
  openForProposal(proposal: any, property: any, allAgents: any[]) {
    this.proposalData = proposal;
    this.propertyDataModal = property;

    // Normalize agents to consistent field names
    this.internalAllAgents = (allAgents ?? []).map((a: any) => ({
      ...a,
      isListingAgent: !!(a.isListingAgent ?? a.isListing),
      isSellingAgent: !!(a.isSellingAgent ?? a.isSelling),
      listingCommission: a.listingCommission ?? a.listingCom ?? 0,
      saleCommission: a.saleCommission ?? a.saleCom ?? 0,
      agentFile: a.agentFile ?? a.image ?? "",
    }));

    this.internalSellingSelected = [];
    this.internalListingSelected = [];

    const selling = this.findProposalSenderAgent(
      this.internalAllAgents,
      proposal,
    );
    if (selling) {
      this.internalAllAgents = this.internalAllAgents.map((agent: any) => ({
        ...agent,
        isSellingAgent: this.isSameAgent(agent, selling),
      }));
      this.internalSellingSelected.push(selling);
    } else {
      const selling2 = this.internalAllAgents.find((ag) => ag.isSellingAgent);
      if (selling2) {
        this.internalSellingSelected.push(selling2);
      }
    }
    const listing = this.internalAllAgents.find((ag) => ag.isListingAgent);
    if (listing) {
      this.internalListingSelected.push(listing);
    }

    this.initProposalForm(proposal, property);
    this.syncDealFieldValidation();
    this.scheduleCommissionTargetPreview();
    this.modalService.open(this.checkProposalModalRef, {
      size: "xl",
      centered: true,
      backdropClass: "custom-modal",
    });
  }

  submitCheckProposal() {
    if (this.commissionTargetPreviewLoading) {
      return;
    }

    this.submitedCheck = true;
    const activeForm = this.getActiveForm();
    const proposalId = this.getActiveProposalId();

    if (!activeForm || !proposalId) {
      this.submitedCheck = false;
      Swal.fire({
        title: this.translateService.instant("t-something-wrong"),
        text: this.translateService.instant("t-error-updating"),
        icon: "warning",
        confirmButtonColor: "rgb(54, 69, 116)",
        confirmButtonText: this.translateService.instant("t-close"),
      });
      return;
    }

    this.ensureDealControl(activeForm);
    this.syncDealFieldValidation();

    if (activeForm.invalid) {
      activeForm.markAllAsTouched();
      this.submitedCheck = false;
      return;
    }

    if (this.isSellerRequiredInvalid()) {
      return;
    }

    Swal.fire({
      title: this.translateService.instant("t-proposals.t-check-title"),
      text: this.translateService.instant("t-proposals.t-check-text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#008a00",
      cancelButtonColor: "#f46a6a",
      confirmButtonText: this.translateService.instant(
        "t-proposals.t-check-confirm",
      ),
      cancelButtonText: this.translateService.instant(
        "t-proposals.t-check-cancel",
      ),
    }).then((result) => {
      if (!result.value) return;
      const formValues = this.normalizeSharedSalePayload(activeForm.value);
      const listingAgents = this.normalizeSaleAgentsPayload(
        this.getSelectedAgents("listing"),
      );
      const sellingAgents = this.normalizeSaleAgentsPayload(
        this.getSelectedAgents("selling"),
      );
      const payload = {
        ...formValues,
        listingAgent: listingAgents,
        sellingAgent: sellingAgents,
      };

      this.proposalsService.checkProposal(proposalId, payload).subscribe({
        next: (data: { proposal: any; saleId: any }) => {
          this.submitedCheck = false;
          this.proposalChecked.emit({
            proposalId,
            proposal: data.proposal,
            saleId: data.saleId,
            formValues,
            listingAgents,
            sellingAgents,
            propertyData: this.propertyDataModal,
          });
          this.internalForm?.reset();
          this.internalListingSelected = [];
          this.internalSellingSelected = [];
          this.submitedCheck = false;
        },
        error: (error: any) => {
          if (this.isDealValidationError(error)) {
            const dealControl = this.ensureDealControl(activeForm);
            if (dealControl) {
              dealControl.setErrors({
                ...(dealControl.errors ?? {}),
                invalidDeal: true,
              });
              dealControl.markAsDirty();
              dealControl.markAsTouched();
            }
            this.submitedCheck = false;
            return;
          }

          if (this.isSellerValidationError(error)) {
            this.submitedCheck = true;
            return;
          }

          const translatedError = this.getTranslatedErrorMessage(error);
          this.submitedCheck = false;
          Swal.fire({
            title: this.translateService.instant("t-something-wrong"),
            text:
              translatedError ??
              this.translateService.instant("t-error-updating"),
            icon: "warning",
            confirmButtonColor: "rgb(54, 69, 116)",
            confirmButtonText: this.translateService.instant("t-close"),
          });
        },
      });
    });
  }

  // O exception filter global aninha as respostas de HttpException:
  // { message: { statusCode, message: '<chave i18n>', error } }. Quando a
  // mensagem é uma chave t-... com tradução, mostra-a em vez do erro genérico.
  private getTranslatedErrorMessage(error: any): string | null {
    const messagePayload = error?.error?.message;
    const messageKey =
      typeof messagePayload === "string"
        ? messagePayload
        : messagePayload?.message;
    if (typeof messageKey !== "string" || !messageKey.startsWith("t-")) {
      return null;
    }
    const translated = this.translateService.instant(messageKey);
    return translated && translated !== messageKey ? translated : null;
  }

  // ─── Internal agent management ───────────────────────────────────────
  openAgentsInternal(type: "listing" | "selling") {
    this.agentsSelectedTypeInternal = type;
    this.modalService.open(this.agentsModalInternalRef, {
      size: "md",
      centered: true,
      backdropClass: "z-modal",
      windowClass: "z-modal",
    });
  }

  addAgentListingInternal(agent: any) {
    if (this.isStoreOperatorAgent(agent)) {
      return;
    }

    this.internalAllAgents = this.internalAllAgents.map((a) => ({
      ...a,
      isListingAgent: false,
    }));
    this.internalListingSelected = [agent];
    const found = this.internalAllAgents.find((a) => a.id === agent.id);
    if (found) found.isListingAgent = true;
    this.scheduleCommissionTargetPreview();
  }

  // Delegado ao PermissionsService: um STORE OPERATOR deixa de ser excluído
  // quando a entidade liga "diretores de loja também atuam como consultores".
  private isStoreOperatorAgent(agent: any): boolean {
    return !this.permissionsService.canBeListingAgent(agent);
  }

  removeAgentListingInternal(agent: any) {
    this.internalListingSelected = [];
    const found = this.internalAllAgents.find((a) => a.id === agent.id);
    if (found) found.isListingAgent = false;
    this.scheduleCommissionTargetPreview();
  }

  addAgentSellingInternal(agent: any) {
    if (!this.internalSellingSelected.some((a) => a.id === agent.id)) {
      this.internalSellingSelected.push(agent);
    }
    const found = this.internalAllAgents.find((a) => a.id === agent.id);
    if (found) found.isSellingAgent = true;
    this.scheduleCommissionTargetPreview();
  }

  removeAgentSellingInternal(agent: any) {
    this.internalSellingSelected = this.internalSellingSelected.filter(
      (a) => a.id !== agent.id,
    );
    const found = this.internalAllAgents.find((a) => a.id === agent.id);
    if (found) found.isSellingAgent = false;
    this.scheduleCommissionTargetPreview();
  }

  // ─── Private helpers ─────────────────────────────────────────────────
  private initProposalForm(proposal: any, property: any) {
    this.submitedCheck = false;
    this.internalForm = this.formBuilder.group({
      saleValue: [
        proposal.history ? proposal.history[0]?.value : null,
        [Validators.required, Validators.min(1)],
      ],
      saleCommission: [
        property?.saleCommission ?? null,
        [Validators.required, Validators.min(1)],
      ],
      isPercentageCommission: [property?.isPercentageCommission ?? true],
      saleDate: [
        proposal.history ? proposal.history[0]?.date : proposal.proposalDate,
        [Validators.required],
      ],
      agencyProposal: [proposal.agencyProposal ?? false],
      // Negócio já resolvido pelo host (proposta feita a partir de um deal):
      // segue no payload do check em vez de obrigar a escolher outra vez.
      dealId: [this.formGroup?.get("dealId")?.value ?? null],
    });
    if (proposal.agencyProposal) {
      const lastDiv = proposal.lastAction
        ? proposal.lastAction.saleDivision
        : proposal.history
          ? proposal.history[0]?.saleDivision
          : 50;
      this.internalForm.setControl(
        "saleDivision",
        new FormControl(lastDiv, Validators.required),
      );
      this.internalForm.setControl(
        "partnerName",
        new FormControl(proposal.partnerEntityName, Validators.required),
      );
      // Pré-preenche as condições de partilha externas vindas da proposta:
      // Venda Partilhada ON, nome da agência e % da parceira (100 − divisão).
      this.internalForm.setControl("sharedSale", new FormControl(true));
      this.internalForm.setControl(
        "partnerEntityName",
        new FormControl(proposal.partnerEntityName ?? null),
      );
      this.internalForm.setControl(
        "sharedCommission",
        new FormControl(lastDiv != null ? 100 - Number(lastDiv) : 50),
      );
      this.internalForm.setControl(
        "splitCommissionValue",
        new FormControl(property?.saleCommission ?? null),
      );
    }
    if (proposal.internalShare) {
      // Proposta enviada por outra loja da rede: pré-preenche a Venda
      // Partilhada como Partilha Interna com as condições da proposta.
      const lastDiv = proposal.lastAction
        ? proposal.lastAction.saleDivision
        : proposal.history
          ? proposal.history[0]?.saleDivision
          : 50;
      this.internalForm.setControl("sharedSale", new FormControl(true));
      this.internalForm.setControl("shareType", new FormControl("internal"));
      this.internalForm.setControl(
        "sharedCommission",
        new FormControl(lastDiv != null ? 100 - Number(lastDiv) : 50),
      );
      this.internalForm.setControl(
        "splitCommissionValue",
        new FormControl(property?.saleCommission ?? null),
      );
      this.internalForm.setControl(
        "partnerStoreId",
        new FormControl(proposal.partnerStoreId ?? null),
      );
      this.internalForm.setControl(
        "internalShareSellerId",
        new FormControl(proposal.user?.id ?? proposal.userId ?? null),
      );
      this.internalForm.setControl(
        "internalShareSellerCommission",
        new FormControl(proposal.user?.saleCommission ?? null),
      );
    }
    this.ensureShareModeControls();
    this.syncDealFieldValidation();
    this.syncSaleDateValidation();
    this.bindPreviewToInternalForm();
    this.scheduleCommissionTargetPreview();
  }

  private loadEntitySettings() {
    if (this.lockDealSelection || this.hasLoadedEntitySettings) {
      return;
    }

    this.hasLoadedEntitySettings = true;
    this.entityService
      .getEntityForProposal()
      .pipe(
        timeout(10000),
        catchError((error) => {
          console.error(error);
          this.requiredDealForSale = false;
          this.requiredSellerForSale = false;
          return of(null);
        }),
      )
      .subscribe({
        next: (entity) => {
          this.requiredDealForSale = entity?.requiredDealForSale ?? false;
          this.requiredSellerForSale = entity?.requiredSellerForSale ?? false;
          this.syncDealFieldValidation();
          if (this.requiredDealForSale) {
            this.reloadDealsForSale();
          }
        },
        error: () => {
          this.requiredDealForSale = false;
          this.requiredSellerForSale = false;
        },
      });
  }

  private getCurrentPropertyId(): string | null {
    const activeProperty =
      this.property ??
      this.propertyDataModal ??
      this.proposalData?.property ??
      this.proposal?.property;

    if (!activeProperty?.id) {
      return null;
    }

    return `${activeProperty.id}`;
  }

  private reloadDealsForSale(): void {
    if (this.loadingDeals) {
      this.pendingDealsReload = true;
      return;
    }

    this.dealsPage = 1;
    this.hasMoreDeals = true;
    this.dealsTotal = 0;
    this.dealsList = [];
    this.loadDealsForSale(true);
  }

  private loadDealsForSale(reset = false): void {
    if (this.loadingDeals) {
      return;
    }
    if (!reset && !this.hasMoreDeals) {
      return;
    }

    this.loadingDeals = true;
    const lang = this.cookieService.get("lang") || "pt";
    this.dealsService
      .searchSaleDealsForAssociation(lang, {
        page: this.dealsPage,
        pageSize: this.dealsPageSize,
        search: this.dealSearch,
        propertyId: this.getCurrentPropertyId() ?? undefined,
        association: this.dealAssociationFilter,
        statusId: this.selectedDealStatusId,
        selectedDealId:
          Number(this.getActiveForm()?.get("dealId")?.value) || null,
      })
      .subscribe({
        next: (data: any) => {
          const incoming = Array.isArray(data?.items) ? data.items : [];
          const merged = reset ? incoming : [...this.dealsList, ...incoming];
          const unique = new Map<any, any>();
          merged.forEach((deal: any) => {
            if (deal?.id) {
              unique.set(deal.id, deal);
            }
          });

          this.dealsList = Array.from(unique.values());
          this.dealsTotal = Number(data?.total ?? 0);
          this.dealStatusOptions = Array.isArray(data?.statuses)
            ? data.statuses
            : [];

          const selectedId = Number(this.getActiveForm()?.get("dealId")?.value);
          if (!selectedId || !Number.isFinite(selectedId) || selectedId <= 0) {
            this.selectedDealDetails = null;
          } else if (
            data?.selectedDeal &&
            Number(data.selectedDeal?.id) === selectedId
          ) {
            this.selectedDealDetails = data.selectedDeal;
          } else if (
            !this.selectedDealDetails ||
            Number(this.selectedDealDetails?.id) !== selectedId
          ) {
            this.selectedDealDetails = null;
          }

          this.hasMoreDeals = data?.hasMore === true;
          this.dealsPage += 1;
          this.loadingDeals = false;
          if (this.pendingDealsReload) {
            this.pendingDealsReload = false;
            this.reloadDealsForSale();
          }
        },
        error: () => {
          this.loadingDeals = false;
          if (this.pendingDealsReload) {
            this.pendingDealsReload = false;
            this.reloadDealsForSale();
          }
        },
      });
  }

  private normalizeDecimalInput(value: any): any {
    if (value === null || value === undefined || value === "") {
      return value;
    }

    if (typeof value === "number") {
      return value;
    }

    const compact = `${value}`.replace(/\s/g, "");
    const normalized =
      compact.includes(",") && compact.includes(".")
        ? compact.replace(/\./g, "").replace(",", ".")
        : compact.replace(",", ".");
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : value;
  }

  private normalizeSaleFormNumbers(formValues: any) {
    const normalized = {
      ...formValues,
      saleValue: this.normalizeDecimalInput(formValues?.saleValue),
      saleCommission: this.normalizeDecimalInput(formValues?.saleCommission),
      globalSaleCommission: this.normalizeDecimalInput(
        formValues?.globalSaleCommission,
      ),
      sharedCommission: this.normalizeDecimalInput(
        formValues?.sharedCommission,
      ),
      splitCommissionValue: this.normalizeDecimalInput(
        formValues?.splitCommissionValue,
      ),
      referralCommissionValue: this.normalizeDecimalInput(
        formValues?.referralCommissionValue,
      ),
    };

    if (Array.isArray(formValues?.paymentConditions)) {
      normalized.paymentConditions = formValues.paymentConditions.map(
        (payment: any) => ({
          ...payment,
          value: this.normalizeDecimalInput(payment?.value),
          agencyValue: this.normalizeDecimalInput(payment?.agencyValue),
        }),
      );
    }

    return normalized;
  }

  private normalizeSaleAgentsPayload(agents: any[]) {
    return (agents ?? []).map((agent: any) => ({
      ...agent,
      listingCommission: this.normalizeDecimalInput(agent?.listingCommission),
      listingCom: this.normalizeDecimalInput(agent?.listingCom),
      saleCommission: this.normalizeDecimalInput(agent?.saleCommission),
      saleCom: this.normalizeDecimalInput(agent?.saleCom),
      commission: this.normalizeDecimalInput(agent?.commission),
    }));
  }

  private toDecimalNumber(value: any): number {
    const normalized = this.normalizeDecimalInput(value);
    const parsed = Number(normalized ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private normalizeSharedSalePayload(formValues: any) {
    const normalizedFormValues = this.normalizeSaleFormNumbers(formValues);

    if (!normalizedFormValues?.sharedSale) {
      return {
        ...normalizedFormValues,
        agencyProposal: false,
        shareType: null,
        partnerStoreId: null,
        internalShareSeller: null,
      };
    }

    if (normalizedFormValues?.shareType === "internal") {
      return {
        ...normalizedFormValues,
        agencyProposal: false,
        shareType: "internal",
        partnerEntityName: null,
        partnerStoreId: Number(normalizedFormValues.partnerStoreId) || null,
        internalShareSeller: normalizedFormValues.internalShareSellerId
          ? {
              userId: normalizedFormValues.internalShareSellerId,
              percentage:
                normalizedFormValues.internalShareSellerCommission ?? null,
            }
          : null,
      };
    }

    return {
      ...normalizedFormValues,
      agencyProposal: true,
      shareType: "external",
      partnerStoreId: null,
      internalShareSeller: null,
    };
  }

  private ensureShareModeControls(): void {
    const form = this.getActiveForm();
    if (!form) {
      return;
    }

    if (!form.get("sharedSale")) {
      const legacyAgencyProposal = form.get("agencyProposal")?.value === true;
      form.addControl("sharedSale", new FormControl(legacyAgencyProposal));
    }
    if (!form.get("sharedCommission")) {
      form.addControl("sharedCommission", new FormControl(50));
    }
    if (!form.get("partnerEntityName")) {
      form.addControl(
        "partnerEntityName",
        new FormControl(form.get("partnerName")?.value ?? null),
      );
    }
    if (!form.get("splitCommissionValue")) {
      form.addControl(
        "splitCommissionValue",
        new FormControl(form.get("saleCommission")?.value ?? 0),
      );
    }
    if (!form.get("shareType")) {
      const isInternalProposal =
        (this.proposal?.internalShare ?? this.proposalData?.internalShare) ===
        true;
      form.addControl(
        "shareType",
        new FormControl(isInternalProposal ? "internal" : "external"),
      );
    }
    if (!form.get("partnerStoreId")) {
      form.addControl(
        "partnerStoreId",
        new FormControl(
          this.proposal?.partnerStoreId ??
            this.proposalData?.partnerStoreId ??
            null,
        ),
      );
    }
    if (!form.get("internalShareSellerId")) {
      form.addControl("internalShareSellerId", new FormControl(null));
    }
    if (!form.get("internalShareSellerCommission")) {
      form.addControl("internalShareSellerCommission", new FormControl(null));
    }
    if (!form.get("internalShareSeller")) {
      // Payload pronto para o backend, mantido em sincronia com os controls
      // internalShareSellerId/Commission — os hosts que enviam o form inteiro
      // (won/details/edição) não precisam de mapear nada.
      form.addControl("internalShareSeller", new FormControl(null));
    }

    if (!this.configuredShareModeForms.has(form)) {
      this.configuredShareModeForms.add(form);
      form.get("sharedSale")?.valueChanges.subscribe(() => {
        this.syncInternalShareState();
      });
      form.get("shareType")?.valueChanges.subscribe(() => {
        this.syncInternalShareState();
      });
      form.get("internalShareSellerId")?.valueChanges.subscribe(() => {
        this.prefillInternalSellerCommission();
        this.syncInternalShareState();
      });
      form.get("internalShareSellerCommission")?.valueChanges.subscribe(() => {
        this.syncInternalShareState();
      });
      form.get("partnerStoreId")?.valueChanges.subscribe(() => {
        this.onInternalStoreChange();
        this.syncInternalShareState();
      });
    }

    this.syncInternalShareState();
    this.syncLegacyAgencyProposalFlag();
  }

  get isInternalShareMode(): boolean {
    const form = this.getActiveForm();
    return (
      form?.get("sharedSale")?.value === true &&
      form?.get("shareType")?.value === "internal"
    );
  }

  get isInternalShareLocked(): boolean {
    return (
      (this.proposalData?.internalShare ?? this.proposal?.internalShare) ===
      true
    );
  }

  get internalShareConsultants(): any[] {
    const storeId = Number(
      this.getActiveForm()?.get("partnerStoreId")?.value,
    );
    if (!storeId) {
      return [];
    }
    return this.internalShareConsultantsByStore.get(storeId) ?? [];
  }

  private syncInternalShareState(): void {
    const form = this.getActiveForm();
    if (!form) {
      return;
    }

    const internal =
      form.get("sharedSale")?.value === true &&
      form.get("shareType")?.value === "internal";
    const partnerStoreControl = form.get("partnerStoreId");
    const sellerControl = form.get("internalShareSellerId");

    // Na partilha interna só a loja parceira é obrigatória. O consultor
    // vendedor é opcional — a regra da entidade "vendedor obrigatório" não se
    // aplica a partilhas internas — e a percentagem dele não se pede: o
    // servidor usa a comissão de venda por defeito do utilizador.
    if (internal) {
      partnerStoreControl?.setValidators([Validators.required]);
      this.loadInternalShareTargets();
    } else {
      partnerStoreControl?.clearValidators();
    }
    sellerControl?.clearValidators();
    partnerStoreControl?.updateValueAndValidity({ emitEvent: false });
    sellerControl?.updateValueAndValidity({ emitEvent: false });

    this.dropHiddenSellingAgents();

    const sellerId = sellerControl?.value;
    form.get("internalShareSeller")?.setValue(
      internal && sellerId
        ? {
            userId: sellerId,
            percentage:
              form.get("internalShareSellerCommission")?.value ?? null,
          }
        : null,
      { emitEvent: false },
    );

    this.syncLegacyAgencyProposalFlag();
  }

  private getListingStoreId(): number | null {
    const activeProperty =
      this.property ??
      this.propertyDataModal ??
      this.proposalData?.property ??
      this.proposal?.property;
    const id = Number(
      activeProperty?.store?.id ??
        activeProperty?.storeId ??
        activeProperty?.store,
    );
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  private loadInternalShareTargets(): void {
    if (this.internalShareTargetsLoaded || this.loadingInternalShareTargets) {
      this.refreshInternalShareStores();
      return;
    }

    this.loadingInternalShareTargets = true;
    this.dealsService.getDealReferralTargets().subscribe({
      next: (data: any) => {
        this.loadingInternalShareTargets = false;
        this.internalShareTargetsLoaded = true;
        const stores = Array.isArray(data?.stores) ? data.stores : [];
        this.internalShareConsultantsByStore.clear();
        for (const store of stores) {
          const consultants = [
            ...(store?.consultants ?? []),
            ...(store?.defaultStoreOperator
              ? [store.defaultStoreOperator]
              : []),
          ]
            .filter(
              (consultant: any, index: number, list: any[]) =>
                consultant?.id &&
                list.findIndex((c) => c?.id === consultant.id) === index,
            )
            .map((consultant: any) => ({
              id: consultant.id,
              name: consultant.name,
              saleCommission: consultant.saleCommission ?? null,
            }));
          this.internalShareConsultantsByStore.set(
            Number(store.id),
            consultants,
          );
        }
        this.allInternalShareStores = stores.map((store: any) => ({
          id: Number(store.id),
          name: store.name,
        }));
        this.refreshInternalShareStores();
        this.prefillInternalSellerCommission();
      },
      error: () => {
        this.loadingInternalShareTargets = false;
      },
    });
  }

  private refreshInternalShareStores(): void {
    const listingStoreId = this.getListingStoreId();
    this.internalShareStores = this.allInternalShareStores.filter(
      (store) => store.id !== listingStoreId,
    );
  }

  onInternalStoreChange(): void {
    const sellerControl = this.getActiveForm()?.get("internalShareSellerId");
    const sellerId = sellerControl?.value;
    if (
      sellerId &&
      !this.internalShareConsultants.some((c) => c.id === sellerId)
    ) {
      sellerControl?.setValue(null);
    }
  }

  // Pré-preenche a % do consultor vendedor com o default do UserEntity
  // (devolvido pelo endpoint de targets) quando o campo está vazio.
  private prefillInternalSellerCommission(): void {
    const form = this.getActiveForm();
    if (!form) {
      return;
    }
    const sellerId = form.get("internalShareSellerId")?.value;
    if (!sellerId) {
      return;
    }
    const control = form.get("internalShareSellerCommission");
    const current = Number(control?.value);
    if (Number.isFinite(current) && current > 0) {
      return;
    }
    const consultant = this.internalShareConsultants.find(
      (c) => c.id === sellerId,
    );
    if (consultant?.saleCommission != null) {
      control?.setValue(Number(consultant.saleCommission));
    }
  }

  private syncLegacyAgencyProposalFlag(): void {
    const form = this.getActiveForm();
    if (!form?.get("agencyProposal") || !form?.get("sharedSale")) {
      return;
    }
    form.get("agencyProposal")?.setValue(
      form.get("sharedSale")?.value === true &&
        form.get("shareType")?.value !== "internal",
      { emitEvent: false },
    );
  }

  private syncDealFieldValidation(): void {
    const forms = [this.formGroup, this.internalForm];
    for (const form of forms) {
      if (!form) {
        continue;
      }
      const control = this.ensureDealControl(form);
      if (!control) {
        continue;
      }

      control.clearValidators();
      this.clearDealValidationErrors(control);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  private clearDealValidationErrors(control: FormControl): void {
    const errors = control.errors;
    if (!errors) {
      return;
    }

    const remainingErrors = { ...errors };
    delete remainingErrors["required"];
    delete remainingErrors["invalidDeal"];
    control.setErrors(
      Object.keys(remainingErrors).length ? remainingErrors : null,
    );
  }

  private ensureDealControl(form: FormGroup | null): FormControl | null {
    if (!form || this.lockDealSelection) {
      return null;
    }

    let control = form.get("dealId") as FormControl | null;
    if (!control) {
      control = new FormControl(null);
      form.addControl("dealId", control);
    }

    return control;
  }

  private isDealValidationError(error: any): boolean {
    const rawMessage =
      error?.error?.message ?? error?.message ?? error?.error ?? "";
    const message = Array.isArray(rawMessage)
      ? rawMessage.join(" ").toLowerCase()
      : `${rawMessage}`.toLowerCase();

    if (!message || !message.includes("deal")) {
      return false;
    }

    return (
      message.includes("obrig") ||
      message.includes("required") ||
      message.includes("inv") ||
      message.includes("invalid")
    );
  }

  private isSellerValidationError(error: any): boolean {
    const rawMessage =
      error?.error?.message ?? error?.message ?? error?.error ?? "";
    const message = Array.isArray(rawMessage)
      ? rawMessage.join(" ").toLowerCase()
      : `${rawMessage}`.toLowerCase();

    return message.includes("t-sale-seller-required");
  }

  private syncSaleDateValidation(): void {
    const forms = [this.formGroup, this.internalForm];
    for (const form of forms) {
      if (!form) {
        continue;
      }

      const control = form.get("saleDate");
      if (!control) {
        continue;
      }

      if (this.configuredSaleDateControls.has(control)) {
        continue;
      }

      this.configuredSaleDateControls.add(control);
      control.addValidators(this.saleDateRoleValidator);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  private normalizeReferralCommissionValueType(
    value: any,
  ): "PERCENTAGE" | "FIXED" {
    return `${value ?? ""}`.toUpperCase() === "FIXED" ? "FIXED" : "PERCENTAGE";
  }

  private syncReferralCommissionControls(): void {
    const forms = [this.formGroup, this.internalForm];
    for (const form of forms) {
      if (!form) {
        continue;
      }

      if (form.contains("referralCommissionSource")) {
        form.removeControl("referralCommissionSource");
      }

      if (!this.showReferralCommissionSection) {
        if (form.contains("referralCommissionValueType")) {
          form.removeControl("referralCommissionValueType");
        }
        if (form.contains("referralCommissionValue")) {
          form.removeControl("referralCommissionValue");
        }
        continue;
      }

      const valueType = this.normalizeReferralCommissionValueType(
        this.referralCommissionInitial?.valueType,
      );
      const value = this.referralCommissionInitial?.value ?? 0;

      if (!form.contains("referralCommissionValueType")) {
        form.addControl(
          "referralCommissionValueType",
          new FormControl(valueType, [Validators.required]),
        );
      } else if (!form.get("referralCommissionValueType")?.dirty) {
        form.get("referralCommissionValueType")?.setValue(valueType, {
          emitEvent: false,
        });
      }

      if (!form.contains("referralCommissionValue")) {
        form.addControl(
          "referralCommissionValue",
          new FormControl(value, [Validators.min(0)]),
        );
      } else if (!form.get("referralCommissionValue")?.dirty) {
        form.get("referralCommissionValue")?.setValue(value, {
          emitEvent: false,
        });
      }
    }
  }

  private bindPreviewToExternalForm(): void {
    this.formGroupValueSubscription?.unsubscribe();

    if (!this.formGroup) {
      return;
    }

    this.formGroupValueSubscription = this.formGroup.valueChanges.subscribe(
      () => {
        this.scheduleCommissionTargetPreview();
      },
    );
  }

  private bindPreviewToInternalForm(): void {
    this.internalFormValueSubscription?.unsubscribe();

    if (!this.internalForm) {
      return;
    }

    this.internalFormValueSubscription =
      this.internalForm.valueChanges.subscribe(() => {
        this.scheduleCommissionTargetPreview();
      });
  }

  private scheduleCommissionTargetPreview(): void {
    this.commissionTargetPreviewSubject.next();
  }

  private scheduleCommissionTargetPreviewAsync(): void {
    setTimeout(() => {
      this.scheduleCommissionTargetPreview();
    }, 0);
  }

  private buildSelectedAgentsForCommissionTargetPreview(): any[] {
    const merged = new Map<string, any>();

    const mergeAgent = (agent: any, side: "listing" | "selling") => {
      const identifier = this.normalizeIdentifier(
        agent?.userId ?? agent?.id ?? agent?.agentId ?? agent?.user?.id,
      );

      if (!identifier) {
        return;
      }

      const current = merged.get(identifier) ?? {
        id: agent?.id ?? agent?.userId ?? agent?.agentId ?? agent?.user?.id,
        userId: identifier,
        isListingAgent: false,
        isSellingAgent: false,
        listingCommission: this.toDecimalNumber(
          agent?.listingCommission ?? agent?.listingCom ?? 0,
        ),
        saleCommission: this.toDecimalNumber(
          agent?.saleCommission ?? agent?.saleCom ?? 0,
        ),
      };

      current.isListingAgent =
        current.isListingAgent ||
        side === "listing" ||
        agent?.isListingAgent === true;
      current.isSellingAgent =
        current.isSellingAgent ||
        side === "selling" ||
        agent?.isSellingAgent === true;

      if (side === "listing") {
        current.listingCommission = this.toDecimalNumber(
          agent?.listingCommission ?? agent?.listingCom ?? 0,
        );
      } else {
        current.saleCommission = this.toDecimalNumber(
          agent?.saleCommission ?? agent?.saleCom ?? 0,
        );
      }

      merged.set(identifier, {
        ...agent,
        ...current,
      });
    };

    this.getSelectedAgents("listing").forEach((agent: any) => {
      mergeAgent(agent, "listing");
    });
    this.getSelectedAgents("selling").forEach((agent: any) => {
      mergeAgent(agent, "selling");
    });

    return Array.from(merged.values()).filter(
      (agent: any) =>
        agent?.isListingAgent === true || agent?.isSellingAgent === true,
    );
  }

  private buildCommissionTargetPreviewPayload(): any | null {
    const form = this.getActiveForm();

    if (!form || this.isRentMode || !this.showAgentSections) {
      return null;
    }

    const agents = this.buildSelectedAgentsForCommissionTargetPreview();

    if (!agents.length) {
      return null;
    }

    return {
      saleValue: this.normalizeDecimalInput(form.get("saleValue")?.value),
      saleDate: form.get("saleDate")?.value ?? null,
      saleCommission: this.normalizeDecimalInput(
        form.get("saleCommission")?.value,
      ),
      isPercentageCommission:
        form.get("isPercentageCommission")?.value !== false,
      sharedSale: form.get("sharedSale")?.value === true,
      sharedCommission: this.normalizeDecimalInput(
        form.get("sharedCommission")?.value,
      ),
      splitCommissionValue: this.normalizeDecimalInput(
        form.get("splitCommissionValue")?.value,
      ),
      agents: agents.map((agent: any) => ({
        id: agent?.id ?? agent?.userId,
        userId: agent?.userId ?? agent?.id,
        isListingAgent: agent?.isListingAgent === true,
        isSellingAgent: agent?.isSellingAgent === true,
        listingCommission: this.toDecimalNumber(agent?.listingCommission ?? 0),
        saleCommission: this.toDecimalNumber(agent?.saleCommission ?? 0),
      })),
    };
  }

  private applyCommissionTargetPreview(previewAgents: any[]): void {
    const previewMap = new Map<string, any>(
      (previewAgents ?? []).map((preview: any) => [
        this.normalizeIdentifier(preview?.userId),
        preview,
      ]),
    );

    const applyToAgent = (agent: any, side: "listing" | "selling") => {
      const identifier = this.normalizeIdentifier(
        agent?.userId ?? agent?.id ?? agent?.agentId ?? agent?.user?.id,
      );
      const preview = previewMap.get(identifier);

      if (!preview?.usedCommissionTarget) {
        agent.commissionTargetEnabled = false;
        agent.commissionTargetCurrentPercent = null;
        agent.commissionTargetNextPercent = null;
        agent.commissionTargetPreviousPercent = null;
        agent.commissionTargetChangedSinceLastSale = false;
        agent.commissionTargetWillChangeOnNextSale = false;
        return;
      }

      // Comissão definida no utilizador sobrepõe a % da meta: o backend envia
      // o valor efetivo por lado e o flag de override (sem avisos de escalão).
      const overridden =
        side === "listing"
          ? preview?.userOverrideListing === true
          : preview?.userOverrideSelling === true;
      const effectivePercent = Number(
        (side === "listing"
          ? preview?.effectiveListingPercent
          : preview?.effectiveSellingPercent) ??
          preview?.currentPercent ??
          0,
      );

      agent.commissionTargetEnabled = true;
      agent.commissionTargetCurrentPercent = effectivePercent;
      agent.commissionTargetNextPercent = preview?.nextPercent ?? null;
      agent.commissionTargetPreviousPercent = preview?.previousPercent ?? null;
      agent.commissionTargetChangedSinceLastSale =
        !overridden && preview?.changedSinceLastSale === true;
      agent.commissionTargetWillChangeOnNextSale =
        !overridden && preview?.willChangeOnNextSale === true;

      if (side === "listing") {
        agent.listingCommission = effectivePercent;
      } else {
        agent.saleCommission = effectivePercent;
      }
    };

    this.getSelectedAgents("listing").forEach((agent: any) => {
      applyToAgent(agent, "listing");
    });
    this.getSelectedAgents("selling").forEach((agent: any) => {
      applyToAgent(agent, "selling");
    });
  }

  private getActiveForm(): FormGroup | null {
    if (this.internalForm) {
      return this.internalForm;
    }
    if (this.formGroup) {
      return this.formGroup;
    }
    return null;
  }

  private getActiveProposalId(): any {
    return this.proposal?.id ?? this.proposalData?.id ?? null;
  }

  private getSelectedAgents(type: "listing" | "selling"): any[] {
    // Secção do vendedor escondida (venda partilhada) = sem vendedor. O que
    // ficou selecionado antes de ligar a partilha não pode seguir às
    // escondidas para o servidor.
    if (type === "selling" && !this.showSellerSection()) {
      return [];
    }
    if (this.internalForm) {
      return type === "listing"
        ? this.internalListingSelected
        : this.internalSellingSelected;
    }
    return this.getAgents(type);
  }

  /**
   * Retira os vendedores selecionados quando a secção deixa de estar visível.
   * Sem isto, ligar "Venda partilhada" só escondia o vendedor: continuava no
   * estado do host (won/detalhe) e era criado com a venda + a partilha.
   *
   * No dumb mode o host é o dono da lista: pede-se-lhe a remoção pelo mesmo
   * output do botão "remover". Fica adiado para um microtask porque este
   * método também corre durante a inicialização (ngOnInit/ngOnChanges) e
   * mexer no estado do host a meio da deteção de alterações rebentava com
   * ExpressionChangedAfterItHasBeenChecked.
   */
  private dropHiddenSellingAgents(): void {
    if (this.showSellerSection()) {
      return;
    }

    if (this.internalForm) {
      if (this.internalSellingSelected.length) {
        this.internalSellingSelected = [];
      }
      return;
    }

    const ids = this.getAgents("selling")
      .map((agent: any) => agent?.id)
      .filter((id: any) => id !== null && id !== undefined);
    if (!ids.length) {
      return;
    }

    queueMicrotask(() => {
      // O utilizador pode ter voltado a desligar a partilha entretanto.
      if (this.showSellerSection()) {
        return;
      }
      for (const id of ids) {
        this.removeAgent.emit({ type: "selling", id });
      }
      this.selected.selling = null;
      if (this.openNewAgent) {
        this.openNewAgent.selling = false;
      }
      this.scheduleCommissionTargetPreviewAsync();
    });
  }

  private normalizeIdentifier(value: any): string {
    if (value === null || value === undefined) {
      return "";
    }
    return `${value}`.trim();
  }

  private isSameAgent(agentA: any, agentB: any): boolean {
    const candidatesA = [
      agentA?.id,
      agentA?.agentId,
      agentA?.userId,
      agentA?.user?.id,
    ]
      .map((value) => this.normalizeIdentifier(value))
      .filter(Boolean);

    const candidatesB = [
      agentB?.id,
      agentB?.agentId,
      agentB?.userId,
      agentB?.user?.id,
    ]
      .map((value) => this.normalizeIdentifier(value))
      .filter(Boolean);

    if (!candidatesA.length || !candidatesB.length) {
      return false;
    }

    return candidatesA.some((value) => candidatesB.includes(value));
  }

  private matchesProposalSender(agent: any, proposal: any): boolean {
    const proposalEmail = this.normalizeIdentifier(
      proposal?.user?.email ?? proposal?.userEmail ?? proposal?.consultantEmail,
    ).toLowerCase();

    const agentEmails = [agent?.email, agent?.user?.email]
      .map((value) => this.normalizeIdentifier(value).toLowerCase())
      .filter(Boolean);

    if (proposalEmail && agentEmails.includes(proposalEmail)) {
      return true;
    }

    const proposalCandidates = [
      proposal?.userId,
      proposal?.user?.id,
      proposal?.proposalUserId,
      proposal?.consultantId,
    ]
      .map((value) => this.normalizeIdentifier(value))
      .filter(Boolean);

    const agentCandidates = [
      agent?.id,
      agent?.agentId,
      agent?.userId,
      agent?.user?.id,
    ]
      .map((value) => this.normalizeIdentifier(value))
      .filter(Boolean);

    if (!proposalCandidates.length || !agentCandidates.length) {
      return false;
    }

    return agentCandidates.some((value) => proposalCandidates.includes(value));
  }

  private findProposalSenderAgent(agents: any[], proposal: any): any | null {
    if (!Array.isArray(agents) || !agents.length) {
      return null;
    }

    const found = agents.find((agent) =>
      this.matchesProposalSender(agent, proposal),
    );
    return found ?? null;
  }

  private canSelectPastSaleDate(): boolean {
    return (
      this.isMasterUser ||
      this.permissionsService.hasPermission("CHANGE_SALE_DATE")
    );
  }

  private resolveIsMasterUser(): boolean {
    const roleFromPermissions = `${this.permissionsService.currentRole ?? ""}`
      .trim()
      .toUpperCase();
    if (roleFromPermissions) {
      return roleFromPermissions === "MASTER";
    }

    try {
      const user = JSON.parse(localStorage.getItem("currentUser") || "{}");
      return `${user?.role?.name ?? ""}`.trim().toUpperCase() === "MASTER";
    } catch (_) {
      return false;
    }
  }

  private parseDateValue(value: any): Date | null {
    if (!value) {
      return null;
    }

    const parsedDate =
      value instanceof Date ? new Date(value) : new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return this.getDateStart(parsedDate);
  }

  private getDateStart(date: Date): Date {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }

  private getCurrentEntityId(): number | null {
    const user = this.propertiesService.getUserAuth();
    const fallbackEntity =
      this.property?.entityId ??
      this.proposalData?.entityId ??
      this.proposal?.entityId ??
      null;
    const entityId = Number(user?.entity ?? fallbackEntity);
    if (!Number.isFinite(entityId) || entityId <= 0) {
      return null;
    }
    return entityId;
  }
}
